// AssistedWorkout.js
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ArrowRight, ArrowLeft, ArrowDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import StepOne from './assistedWorkoutSteps/step1';
import StepTwo from './assistedWorkoutSteps/step2';
import StepThree from './assistedWorkoutSteps/step3';
import StepFour from './assistedWorkoutSteps/step4';
import StepFive from './assistedWorkoutSteps/step5';
import StepSix from './assistedWorkoutSteps/step6';
import StepSeven from './assistedWorkoutSteps/step7';
import StepEight from './assistedWorkoutSteps/step8';
import { useWorkout } from './WorkoutContext';
import LightningEffect from './LightningEffect';

const STEPS = [
  { id: 1, label: 'BASICS' },
  { id: 2, label: 'GOALS' }, 
  { id: 3, label: 'EXPERIENCE' },
  { id: 4, label: 'SCHEDULE' },
  { id: 5, label: 'EQUIPMENT' },
  { id: 6, label: 'REVIEW' },
  { id: 7, label: 'GENERATING' },
  { id: 8, label: 'COMPLETE' },
];

const ProgressBar = ({ currentStep, steps }) => {
  const calculateProgress = (current, total) => {
    if (current === 0) return 0;
    return `${(current / (total - 1)) * 100}%`;
  };

  return (
    <div className="flex-1 mx-4">
      <div className="relative h-2.5 rounded-full overflow-hidden aw-progress-track">
        <div
          className="absolute top-0 left-0 h-full rounded-full aw-progress-fill"
          style={{ width: calculateProgress(currentStep, steps.length) }}
        />
      </div>
      <div className="text-center text-[11px] font-semibold text-red-600 mt-1.5">
        Step {currentStep + 1} of {steps.length}
      </div>
    </div>
  );
};

const PROFILE_STORAGE_KEY = 'assistedWorkout:profile';

const loadStoredProfile = () => {
  try {
    const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      name: typeof parsed.name === 'string' ? parsed.name : '',
      gender: typeof parsed.gender === 'string' ? parsed.gender : '',
      ageGroup: typeof parsed.ageGroup === 'string' ? parsed.ageGroup : ''
    };
  } catch {
    return {};
  }
};

const AssistedWorkout = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [slideDirection, setSlideDirection] = useState('left');
  const { setShowFeedbackHints, workoutStrategy, workoutFinal } = useWorkout();
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [burstTick, setBurstTick] = useState(0);
  const [burstActive, setBurstActive] = useState(false);
  const containerRef = useRef(null);

  // Trigger a fullscreen lightning burst (~1.4s)
  const triggerBurst = useCallback(() => {
    setBurstTick(t => t + 1);
    setBurstActive(true);
  }, []);

  useEffect(() => {
    if (!burstActive) return;
    const id = setTimeout(() => setBurstActive(false), 1400);
    return () => clearTimeout(id);
  }, [burstActive, burstTick]);

  // Ambient lightning while we're awaiting the AI:
  //  - currentStep 6 (strategy review screen) and strategy not yet arrived
  //  - currentStep 7 (final plan screen) and final not yet arrived
  const ambientActive =
    (currentStep === 6 && !workoutStrategy) ||
    (currentStep === 7 && !workoutFinal);

  const lightningMode = burstActive ? 'burst' : (ambientActive ? 'ambient' : 'off');
  const [formData, setFormData] = useState(() => {
    const stored = loadStoredProfile();
    return {
      name: stored.name || '',
      gender: stored.gender || '',
      ageGroup: stored.ageGroup || '',
      objective: '',
      focusAreas: [],
      experienceLevel: '',
      limitations: '',
      daysPerWeek: null,
      exercisesPerDay: 6,
      timePerSession: 60,
      equipment: [],
      split: '',
      additionalNotes: '',
      followUpPlan: null,
      supersetsLevel: null
    };
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        PROFILE_STORAGE_KEY,
        JSON.stringify({
          name: formData.name || '',
          gender: formData.gender || '',
          ageGroup: formData.ageGroup || ''
        })
      );
    } catch {
      // ignore quota / privacy-mode errors
    }
  }, [formData.name, formData.gender, formData.ageGroup]);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const hasScrollableContent = scrollHeight > clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20; // 20px threshold
      setShowScrollIndicator(hasScrollableContent && !isAtBottom && scrollTop < scrollHeight / 2);
    };
   
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => container.removeEventListener('scroll', handleScroll);
    }
   }, []);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };
  

  const handleBack = useCallback(() => {
    if (currentStep === 6) {
      setSlideDirection('right');
      setCurrentStep(5);
    } else if (currentStep === 7) {
      setSlideDirection('right');
      setCurrentStep(5);
    } else {
      setSlideDirection('right');
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep, setShowFeedbackHints]);
  


const handleGenerateStart = () => {
  triggerBurst();
  setSlideDirection('left');
  setCurrentStep(6);
  setShowFeedbackHints(false);
};

  const handleGenerateFinalStart = () => {
    triggerBurst();
    setSlideDirection('left');
    setCurrentStep(7);
  };

  const handleGenerateError = () => {
    setSlideDirection('right');
    setCurrentStep(5);
  };

  const updateFields = useCallback((fields) => {
    setFormData(prev => ({ ...prev, ...fields }));
  }, []);


  const isStepValid = () => {
    if (currentStep === 0) {
      const { name, gender, ageGroup } = formData;
      return name?.trim() && gender && ageGroup;
    }
    if (currentStep === 1) {
      const { objective, focusAreas } = formData;
      return objective && focusAreas.length > 0;
    }
    if (currentStep === 2) {
      const { experienceLevel } = formData;
      return experienceLevel;
    }
    if (currentStep === 3) {
      const { daysPerWeek } = formData;  // Only check daysPerWeek
      return daysPerWeek !== null;  // Changed to explicitly check for null
    }
    if (currentStep === 4) {
      const { equipment } = formData;
      return equipment && equipment.length > 0;
    }
    return false;
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction === 'left' ? 500 : -500,
      opacity: 0,
      position: 'absolute',
      width: '100%',
      top: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      position: 'relative',
      width: '100%'
    },
    exit: (direction) => ({
      x: direction === 'left' ? -500 : 500,
      opacity: 0,
      position: 'absolute',
      width: '100%',
      top: 0
    })
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <StepOne formData={formData} updateFields={updateFields} />;
      case 1:
        return <StepTwo formData={formData} updateFields={updateFields} />;
      case 2:
        return <StepThree formData={formData} updateFields={updateFields} />;
      case 3:
        return <StepFour formData={formData} updateFields={updateFields} />;
      case 4:
        return <StepFive formData={formData} updateFields={updateFields} />;
        case 5:
          return (
            <StepSix
              formData={formData}
              updateFields={updateFields}
              onBack={handleBack}
              onGenerate={handleGenerateStart}
            />
          );
        case 6:
          return (
            <StepSeven
              formData={formData}
              updateFields={updateFields}
              onBack={handleBack}
              onError={handleGenerateError}
              onGenerateFinal={handleGenerateFinalStart}
            />
          );
        
        case 7:
          return <StepEight />;
        default:
          return null;
      }
    };



  return (
    <div ref={containerRef} className="min-h-screen mb-20 sm:mb-0 p-6 overflow-auto sm:pb-6"
      style={{
        background: 'radial-gradient(120% 90% at 50% 0%, #ffffff 0%, #f3f3f7 60%, #e8e8ee 100%)'
      }}
    >
    <LightningEffect mode={lightningMode} />
    <div className=" sm:min-w-[600px] max-w-xl mx-auto aw-card rounded-3xl p-8 flex flex-col overflow-y-hidden relative z-10">
      <div className="flex flex-col items-center text-center justify-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold aw-title">AI-Assisted Workout Plan</h1>
        </div>

        <div className="sm:min-h-[620px] min-h-[420px] sm:h-auto w-full relative overflow-y-hidden">
          <AnimatePresence mode="wait" initial={false} custom={slideDirection}>
            <motion.div
              key={currentStep}
              custom={slideDirection}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              className="absolute inset-0 h-full"
            >
              <div className="h-full overflow-y-hidden">
                <div className="h-auto">
                  {renderStep()}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {currentStep < 8 && (
  <div className="flex items-center mt-8 px-4 py-3 rounded-full"
    style={{
      background: 'linear-gradient(180deg, #1a1a1f 0%, #2a2a30 100%)',
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.6)',
      border: '1px solid #0a0a0c'
    }}
  >
    <button
      className={`aw-round-btn w-10 h-10
        ${currentStep === 0 ? 'opacity-0 pointer-events-none' : 'aw-round-light'}`}
      onClick={() => {
        if (currentStep > 0) {
          setSlideDirection('right');
          setCurrentStep(prev => prev - 1);
        }
      }}
    >
      <ArrowLeft className="w-5 h-5" />
    </button>

    <ProgressBar currentStep={currentStep} steps={STEPS} />

    <button
      className={`aw-round-btn w-11 h-11
        ${isStepValid() ? 'aw-round-red' : 'aw-round-disabled'}`}
      onClick={() => {
        if (isStepValid()) {
          setSlideDirection('left');
          setCurrentStep(prev => prev + 1);
        }
      }}
    >
      <ArrowRight className="w-5 h-5" />
    </button>
  </div>

)}
        {showScrollIndicator && (
          <button
            onClick={scrollToBottom}
            className="aw-round-btn aw-round-red fixed left-1/2 -translate-x-1/2 z-50 bottom-6 w-11 h-11"
          >
            <ArrowDown className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AssistedWorkout;